import Dimmer from '@shared/components/ui/Dimmer';
import { ModalManager } from '@shared/components/providers/ModalProvider';
import PageView from '@shared/components/PageView';
import { getLabel, getMessage } from '@shared/utils';
import { Alert, AlertTitle, Step, StepLabel, Stepper } from '@mui/material';
import { createCorrespondentTypeRole, updateCorrespondentTypeRole } from '@shared/services/api/rolesService';
import React, { FC, useState } from 'react';
import AssignPermissionsStep from './AssignPermissionsStep';
import CreationRoleStep from './CreationRoleStep';

interface CorrespondentRole {
  role_id?: number;
  role_name?: string;
  correspondent_type_ids?: number[];
  permissions?: number[];
}

interface RolesFormStepperProps {
  role?: any;
  onSuccess: () => void;
}

const titles = [getLabel('roleManagementStepOne'), getLabel('roleManagementStepTwo')];

const RolesFormStepper: FC<RolesFormStepperProps> = ({ role, onSuccess }) => {
  const editMode = role?.role_id !== undefined;
  let initialRole = {};
  if (editMode) {
    const { role_id, role_name, correspondent_types, permissions: prevPermissions } = role;
    const correspondent_type_ids = correspondent_types.map((item: any) => item.id);
    const permissions = prevPermissions.map((item: any) => item.id);
    initialRole = { role_id, role_name, correspondent_type_ids, permissions };
  }

  const [correspondentRole, setCorrespondentRole] = useState<CorrespondentRole>(initialRole);
  const [activeStep, setActiveStep] = React.useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorRequest, setErrorRequest] = useState<{ code?: string; message?: string } | null>(null);

  const getStepTitle = (step: number) => {
    return titles[step];
  };

  const handleNext = () => {
    setErrorRequest(null);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setErrorRequest(null);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onCompleteForm = (data: any) => {
    const newData = { ...correspondentRole, ...data };
    setCorrespondentRole(newData);
  };

  const onHandleSubmit = async () => {
    setIsLoading(true);
    let response;
    if (editMode) {
      const { role_id: id, role_name, permissions, correspondent_type_ids } = correspondentRole;
      response = await updateCorrespondentTypeRole({ id, role_name, permissions, correspondent_type_ids });
    } else {
      response = await createCorrespondentTypeRole(correspondentRole);
    }

    if (response?.errors?.length) {
      const [errorData] = response.errors;
      setErrorRequest(errorData);
      onSuccess();
    } else {
      onSuccess();
      ModalManager.closeModal();
    }

    setIsLoading(false);
  };

  const handleSteps = (step: number) => {
    switch (step) {
      case 0:
        return (
          <CreationRoleStep correspondentRole={correspondentRole} handleNext={handleNext} onComplete={onCompleteForm} />
        );
      case 1:
        return (
          <AssignPermissionsStep
            correspondentRole={correspondentRole}
            handleBack={handleBack}
            onComplete={onCompleteForm}
            handleSubmit={onHandleSubmit}
          />
        );
      default:
        throw new Error('Unknown step');
    }
  };

  return (
    <Dimmer active={isLoading}>
      <PageView title={editMode ? getLabel('editRole') : getLabel('addRole')}>
        <PageView.Content>
          {getStepTitle(activeStep)}
          <Stepper activeStep={activeStep} sx={{ pt: 3 }} nonLinear>
            {titles.map((label) => (
              <Step key={label}>
                <StepLabel key={label} />
              </Step>
            ))}
          </Stepper>
          {errorRequest && (
            <Alert severity="error">
              <AlertTitle>{getLabel('error')}</AlertTitle>
              {(errorRequest.code ? getMessage(errorRequest.code) : null) ?? errorRequest.message}
            </Alert>
          )}
          {handleSteps(activeStep)}
        </PageView.Content>
      </PageView>
    </Dimmer>
  );
};

export default RolesFormStepper;
